import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import RoomStatus from "../RoomStatusBsr"
import Loading from "../Loading"
import "./RoomStatusList.scss"
import FontAwesome from "react-fontawesome"
import { ComplianceCircle, NO_DATA_BLURB } from "@swipesense/compliance-helpers"
import {
  Table,
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  SortDirection,
  defaultTableHeaderRenderer,
} from "react-virtualized"
import {
  noOccupancy,
  noRNDetected,
  noVisitsDetected,
} from "../../helpers/sharedHelperFunctions"
import HelpTooltip from "../HelpTooltip"
import RoomStatusWithData from "../RoomStatus/RoomStatusWithData"

export default class RoomStatusList extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedIndex: -1,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.table.recomputeRowHeights()
  }

  changeHeaderRenderer = props => {
    const defaultElements = defaultTableHeaderRenderer(props)
    const helpElement = (
      <HelpTooltip
        className="ReactVirtualized__Table__headerTruncatedText"
        key="help-tooltip"
      >
        <p>
          <b>Compliance</b> is the percent of expected Rounds completed over the
          corresponding shift. Though this metric is shared by the Nurse and
          Nurse Assistant, a visit by any nurse or nurse assistant is accounted
          for.
        </p>
        <p>
          There is no compliance reported when:
          <ul>
            <li>No Nurse is detected over the entire shift</li>
            <li>No Patient has occupied the room for the entire shift</li>
          </ul>
        </p>
        <p>
          The <FontAwesome name="exclamation-triangle" /> is shown when neither
          a nurse or nursing assistant has been detected in a room that was
          occupied by a patient.
        </p>
      </HelpTooltip>
    )
    return [defaultElements[0], helpElement, ...defaultElements.slice(1)]
  }

  displaysWarningClass = shift => {
    return `none-detected${this.displaysWarning(shift) ? "__alert" : ""}`
  }

  displaysWarning = shift => {
    return (
      (noVisitsDetected(shift) || noRNDetected(shift)) && !noOccupancy(shift)
    )
  }

  locationCellRenderer = ({ cellData }) => {
    return <div key="name">{cellData.name}</div>
  }

  nameCellRenderer = ({ cellData, rowData }) => {
    if (cellData) {
      const name = cellData.firstName + " " + cellData.lastName
      return <div key="name">{name}</div>
    } else if (noOccupancy(rowData)) {
      return <div className="no-occupancy">room not occupied</div>
    } else {
      return (
        <div className={this.displaysWarningClass(rowData)}>none detected</div>
      )
    }
  }

  dateCellRenderer = ({ cellData }) => {
    return <div key="shift">{moment(cellData).format("M/D/YYYY HH:mm")}</div>
  }

  complianceCellRenderer = ({ rowData, cellData }) => {
    if (
      noVisitsDetected(rowData) ||
      noRNDetected(rowData) ||
      noOccupancy(rowData)
    ) {
      return (
        <div className="compliance-summary">
          <span className={this.displaysWarningClass(rowData)}>
            {this.displaysWarning(rowData) && (
              <FontAwesome name="exclamation-triangle" />
            )}{" "}
            N/A
          </span>
        </div>
      )
    } else {
      return (
        <div className="compliance-summary">
          <ComplianceCircle compliance={cellData} />
          <span>{`${Math.round(cellData)}%`}</span>
        </div>
      )
    }
  }

  calculateExpandedRowHeight = (index, selectedIndex, rowData) => {
    if (index !== selectedIndex) return 0
    else if (noVisitsDetected(rowData)) return 50
    else return 250
  }

  roomStatusRowRenderer = (props, selectedIndex) => {
    const { index, rowData, style, key } = props
    if (index === selectedIndex) {
      return (
        <div
          style={{ ...style, display: "flex", flexDirection: "column" }}
          key={key}
        >
          {defaultTableRowRenderer({
            ...props,
            style: { width: style.width, height: 36 },
          })}
          <div
            style={{
              width: style.width,
              height: this.calculateExpandedRowHeight(
                index,
                selectedIndex,
                rowData
              ),
            }}
          >
            {!noVisitsDetected(rowData) && (
              <RoomStatusWithData
                locationId={rowData.location.id}
                startDate={rowData.startsAt}
                endDate={rowData.endsAt}
                shiftAssignmentId={rowData.id}
              />
            )}
            {(noRNDetected(rowData) || noVisitsDetected(rowData)) && (
              <div className="no-rn-note">
                Note: Nurse Rounding compliance is only calculated for patients
                where a Nurse has been detected visiting the patient once during
                the shift. A Nurse may not be detected if they are they are not
                wearing their SwipeSense badge, or their badge is no longer
                functioning. Please contact your program manager or{" "}
                <a href="mailto:support@swipesense.com">
                  support@swipesense.com
                </a>{" "}
                if you believe this is an error.
              </div>
            )}
          </div>
        </div>
      )
    } else {
      return defaultTableRowRenderer(props)
    }
  }

  indexRenderer = ({ rowIndex, rowData }, selectedIndex) => {
    if (!noOccupancy(rowData)) {
      if (rowIndex !== selectedIndex) {
        return <FontAwesome name="caret-right" />
      } else {
        return <FontAwesome name="caret-down" />
      }
    }
  }

  render() {
    return (
      <div style={{ flex: 1, height: "100%" }}>
        <AutoSizer>
          {({ height, width }) => (
            <Table
              className="RoomStatusList"
              rowHeight={({ index }) =>
                this.calculateExpandedRowHeight(
                  index,
                  this.state.selectedIndex,
                  this.props.shiftAssignments[index]
                ) + 36
              }
              headerHeight={32}
              height={height || 500}
              width={width || 1500}
              rowCount={this.props.shiftAssignments.length}
              rowGetter={({ index }) => this.props.shiftAssignments[index]}
              sortBy={"startsAt"}
              data={this.props.shiftAssignments}
              loading={this.props.loading}
              onRowClick={props =>
                this.setState(state => {
                  if (
                    state.selectedIndex === props.index ||
                    noOccupancy(props.rowData)
                  ) {
                    return { selectedIndex: -1 }
                  } else {
                    return { selectedIndex: props.index }
                  }
                })
              }
              noRowsMessage={NO_DATA_BLURB}
              loadingComponent={<Loading />}
              rowRenderer={props =>
                this.roomStatusRowRenderer(props, this.state.selectedIndex)
              }
              ref={ref => (this.table = ref)}
            >
              <Column
                dataKey="index"
                width={15}
                label=""
                cellRenderer={props =>
                  this.indexRenderer(props, this.state.selectedIndex)
                }
                defaultSortDirection={SortDirection.ASC}
              />
              {this.props.entityType !== "location" && (
                <Column
                  dataKey="location"
                  width={10}
                  flexGrow={1}
                  label="Location"
                  cellRenderer={this.locationCellRenderer}
                  defaultSortDirection={SortDirection.ASC}
                />
              )}
              <Column
                dataKey="startsAt"
                width={15}
                flexGrow={1}
                label="Shift Start"
                cellRenderer={this.dateCellRenderer}
                defaultSortDirection={SortDirection.DESC}
              />
              <Column
                dataKey="endsAt"
                width={15}
                flexGrow={1}
                label="Shift End"
                cellRenderer={this.dateCellRenderer}
                defaultSortDirection={SortDirection.DESC}
              />
              <Column
                dataKey="primaryUser"
                width={20}
                flexGrow={1}
                label="Nurse"
                cellRenderer={this.nameCellRenderer}
                defaultSortDirection={SortDirection.ASC}
              />
              <Column
                dataKey="secondaryUser"
                width={20}
                flexGrow={1}
                label="Nurse Assistant"
                cellRenderer={this.nameCellRenderer}
                defaultSortDirection={SortDirection.ASC}
              />
              <Column
                dataKey="compliancePercentage"
                width={10}
                flexGrow={1}
                label="Compliance"
                cellRenderer={this.complianceCellRenderer}
                defaultSortDirection={SortDirection.DESC}
                headerRenderer={this.changeHeaderRenderer}
              />
            </Table>
          )}
        </AutoSizer>
      </div>
    )
  }
}

RoomStatusList.propTypes = {
  shiftAssignments: PropTypes.arrayOf(RoomStatus.propTypes.shiftAssignment)
    .isRequired,
  loading: PropTypes.bool,
  entityType: PropTypes.string,
}

RoomStatusList.defaultProps = {
  ignoreOccupancy: false,
}
